/**
 * Official India states + UTs (ISO 3166-2:IN style codes).
 * Centroids are approximate WGS84 for schematic choropleth only —
 * not a Survey of India extract. Full administrative polygons
 * require licensed boundary asset before cartographic ship.
 */
import type { IndiaStateCode } from './types';

export type IndiaStateDef = {
  code: IndiaStateCode;
  name: string;
  /** Approximate centre latitude */
  lat: number;
  /** Approximate centre longitude */
  lon: number;
};

export const INDIA_STATES: readonly IndiaStateDef[] = [
  { code: 'AN', name: 'Andaman and Nicobar Islands', lat: 11.74, lon: 92.66 },
  { code: 'AP', name: 'Andhra Pradesh', lat: 15.91, lon: 79.74 },
  { code: 'AR', name: 'Arunachal Pradesh', lat: 28.22, lon: 94.73 },
  { code: 'AS', name: 'Assam', lat: 26.2, lon: 92.94 },
  { code: 'BR', name: 'Bihar', lat: 25.1, lon: 85.31 },
  { code: 'CH', name: 'Chandigarh', lat: 30.73, lon: 76.78 },
  { code: 'CT', name: 'Chhattisgarh', lat: 21.28, lon: 81.87 },
  { code: 'DH', name: 'Dadra and Nagar Haveli and Daman and Diu', lat: 20.27, lon: 73.02 },
  { code: 'DL', name: 'Delhi', lat: 28.7, lon: 77.1 },
  { code: 'GA', name: 'Goa', lat: 15.3, lon: 74.12 },
  { code: 'GJ', name: 'Gujarat', lat: 22.26, lon: 71.19 },
  { code: 'HP', name: 'Himachal Pradesh', lat: 31.1, lon: 77.17 },
  { code: 'HR', name: 'Haryana', lat: 29.06, lon: 76.09 },
  { code: 'JH', name: 'Jharkhand', lat: 23.61, lon: 85.28 },
  { code: 'JK', name: 'Jammu and Kashmir', lat: 33.78, lon: 76.58 },
  { code: 'KA', name: 'Karnataka', lat: 15.32, lon: 75.71 },
  { code: 'KL', name: 'Kerala', lat: 10.45, lon: 76.28 },
  { code: 'LA', name: 'Ladakh', lat: 34.23, lon: 77.56 },
  { code: 'LD', name: 'Lakshadweep', lat: 10.57, lon: 72.64 },
  { code: 'MH', name: 'Maharashtra', lat: 19.75, lon: 75.71 },
  { code: 'ML', name: 'Meghalaya', lat: 25.47, lon: 91.37 },
  { code: 'MN', name: 'Manipur', lat: 24.66, lon: 93.91 },
  { code: 'MP', name: 'Madhya Pradesh', lat: 22.97, lon: 78.66 },
  { code: 'MZ', name: 'Mizoram', lat: 23.16, lon: 92.94 },
  { code: 'NL', name: 'Nagaland', lat: 26.16, lon: 94.56 },
  { code: 'OD', name: 'Odisha', lat: 20.95, lon: 85.1 },
  { code: 'PB', name: 'Punjab', lat: 31.15, lon: 75.34 },
  { code: 'PY', name: 'Puducherry', lat: 11.94, lon: 79.81 },
  { code: 'RJ', name: 'Rajasthan', lat: 27.02, lon: 74.22 },
  { code: 'SK', name: 'Sikkim', lat: 27.53, lon: 88.51 },
  { code: 'TG', name: 'Telangana', lat: 18.11, lon: 79.02 },
  { code: 'TN', name: 'Tamil Nadu', lat: 11.13, lon: 78.66 },
  { code: 'TR', name: 'Tripura', lat: 23.94, lon: 91.99 },
  { code: 'UP', name: 'Uttar Pradesh', lat: 26.85, lon: 80.91 },
  { code: 'UT', name: 'Uttarakhand', lat: 30.07, lon: 79.02 },
  { code: 'WB', name: 'West Bengal', lat: 22.99, lon: 87.86 },
] as const;

export const INDIA_STATE_BY_CODE: ReadonlyMap<IndiaStateCode, IndiaStateDef> = new Map(
  INDIA_STATES.map((s) => [s.code, s]),
);

/** SVG projection for India extents (schematic board viewBox 0 0 1000 1100). */
export function projectIndia(lat: number, lon: number): { x: number; y: number } {
  const x = ((lon - 68) / (97.5 - 68)) * 900 + 50;
  const y = ((37.5 - lat) / (37.5 - 6.5)) * 980 + 40;
  return { x, y };
}
